import React from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Stack,
  Alert,
  useTheme,
  Button,
} from "@mui/material";
import { FileText, Globe, Calendar, AlertCircle, Images } from "lucide-react";
import TemplateStatusChip from "../../widgets/TemplateStatusChip";

export const getTemplateName = (template) =>
  template?.templateName || template?.name || "Untitled template";

export const getTemplateStatus = (template) =>
  template?.templateStatus || template?.status || "pending";

const getTemplateDate = (template) =>
  template?.createdAt ||
  template?.createdOn ||
  template?.updatedAt ||
  template?.updatedOn;

const formatTemplateDate = (dateValue, includeTime = false) => {
  if (!dateValue) return "Not available";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Not available";

  return includeTime
    ? date.toLocaleString("en-IN")
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export const getTemplateTypeLabel = (template) => {
  const type = template?.templateType || "standard";
  if (type === "carouselCard") return "Carousel";
  if (type === "richCard") return "Rich card";
  return type.replace(/_/g, " ");
};

const MessageCardPreview = ({ card, cardNumber }) => {
  const title = card?.title || card?.cardTitle;
  const description = card?.description || card?.cardDescription;

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 248,
        width: 248,
        overflow: "hidden",
        border: "1px solid #e1e5ed",
        borderRadius: 2.5,
        scrollSnapAlign: "start",
        bgcolor: "#fff",
      }}
    >
      <CardMediaPreview card={card} />
      <Box sx={{ p: 1.5 }}>
        {cardNumber && (
          <Typography
            sx={{
              mb: 0.65,
              fontSize: 10,
              fontWeight: 800,
              color: "#6d4bd1",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Card {cardNumber}
          </Typography>
        )}
        {title && (
          <Typography
            sx={{ fontSize: 13.5, lineHeight: 1.35, fontWeight: 750 }}
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            sx={{
              mt: 0.75,
              fontSize: 11.5,
              lineHeight: 1.5,
              color: "text.secondary",
              whiteSpace: "pre-line",
            }}
          >
            {description}
          </Typography>
        )}
        <SuggestionButtons suggestions={card?.suggestions || []} />
      </Box>
    </Paper>
  );
};

export const getSuggestionLabel = (suggestion) =>
  suggestion?.action?.text ||
  suggestion?.action?.plainText ||
  suggestion?.reply?.plainText ||
  suggestion?.text ||
  "Action";

const CardMediaPreview = ({ card }) => {
  const media = card?.media || card?.cardMedia || {};
  const contentInfo = media.contentInfo || {};
  const fileUrl = contentInfo.fileUrl || card?.imageUrl;
  const thumbnailUrl = contentInfo.thumbnailUrl;
  const fileType = String(media.fileType || card?.fileType || "").toUpperCase();

  if (fileType === "VIDEO" && fileUrl) {
    return (
      <Box
        component="video"
        src={fileUrl}
        poster={thumbnailUrl}
        controls
        preload="metadata"
        aria-label={card?.title || card?.cardTitle || "RCS card video"}
        sx={{
          width: "100%",
          height: 150,
          display: "block",
          objectFit: "cover",
          bgcolor: "#111827",
        }}
      />
    );
  }

  const imageUrl = thumbnailUrl || fileUrl;
  if (!imageUrl) return null;

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={card?.title || card?.cardTitle || "RCS card media"}
      sx={{
        width: "100%",
        height: 150,
        display: "block",
        objectFit: "cover",
        bgcolor: "#eef2f7",
      }}
    />
  );
};

const SuggestionButtons = ({ suggestions = [] }) => {
  if (!suggestions.length) return null;

  return (
    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
      {suggestions.map((suggestion, index) => (
        <Button
          key={`${getSuggestionLabel(suggestion)}-${index}`}
          variant="outlined"
          size="small"
          tabIndex={-1}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 700,
            pointerEvents: "none",
          }}
        >
          {getSuggestionLabel(suggestion)}
        </Button>
      ))}
    </Stack>
  );
};

export const RcsTemplatePreview = ({ template, showLess = false }) => {
  const theme = useTheme();

  const getTemplateContent = () => {
    if (!template?.content) return null;

    if (
      template.templateType === "carouselCard" ||
      Array.isArray(template.content.cardContents)
    ) {
      return {
        type: "carousel",
        cards: template.content.cardContents || [],
        suggestions: template.content.suggestions || [],
      };
    }

    if (template?.templateType === "standard") {
      const c = template.content;
      if (
        c.documentUrl ||
        template.subtemplateType === "text_message_with_media"
      ) {
        const fileName = c.documentFileName || "attachment";
        const isPdf = fileName.toLowerCase().endsWith(".pdf");
        return {
          type: "textWithMedia",
          documentUrl: c.documentUrl,
          documentFileName: fileName,
          messageText: c.messageText || "",
          messageOrder: c.messageOrder || "text_message_at_top",
          suggestions: c.suggestions || [],
          isPdf,
        };
      }
      if (c.messageText) {
        return {
          type: "plain",
          text: c.messageText,
          suggestions: c.suggestions || [],
        };
      }
    }

    if (template.templateType === "richCard") {
      const card = template.content.cardContent || template.content;
      return {
        type: "standalone",
        card: {
          ...card,
          title: card.title || card.messageText,
          description: card.description || card.messageDescription,
          media:
            card.media ||
            (card.imageUrl
              ? {
                  contentInfo: { fileUrl: card.imageUrl },
                  fileType: card.fileType || "IMAGE",
                }
              : undefined),
        },
        suggestions: card.suggestions || [],
      };
    }

    if (template.content.plainText) {
      return {
        type: "plain",
        text: template.content.plainText,
        suggestions: template.content.suggestions || [],
      };
    }

    if (template.content.richCardDetails) {
      const rc = template.content.richCardDetails;
      if (rc.standalone) {
        return {
          type: "standalone",
          card: rc.standalone.content,
          orientation: rc.standalone.cardOrientation,
          suggestions: rc.standalone.content?.suggestions || [],
        };
      }
      if (rc.carousel) {
        return {
          type: "carousel",
          cards: rc.carousel.contents || [],
          suggestions: rc.carousel.suggestions || [],
        };
      }
    }
    return null;
  };

  const content = getTemplateContent();
  const templateName = getTemplateName(template);
  const templateStatus = getTemplateStatus(template);
  const isRejected = templateStatus.toLowerCase() === "rejected";

  return (
    <Paper
      elevation={0}
      sx={{
        p: showLess ? 0 : { xs: 1.75, sm: 3 },
        border: showLess ? "none" : "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: showLess ? "transparent" : "#fff",
      }}
    >
      {/* Header Info */}
      {!showLess && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" fontWeight="700" gutterBottom>
            {templateName}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
            <Chip
              icon={<Globe size={16} />}
              label={getTemplateTypeLabel(template)}
              variant="outlined"
              size="small"
              sx={{ pl: 0.5 }}
            />
            <Chip
              icon={<Calendar size={16} />}
              label={formatTemplateDate(getTemplateDate(template))}
              variant="outlined"
              size="small"
              sx={{ pl: 0.5 }}
            />
            <TemplateStatusChip status={templateStatus} />
          </Box>
        </Box>
      )}

      {/* Rejection Reason Alert */}
      {!showLess && isRejected && (
        <Alert
          severity="error"
          icon={<AlertCircle size={24} />}
          sx={{ mb: 4, borderRadius: 2, alignItems: "center" }}
        >
          <Typography variant="subtitle2" fontWeight="600">
            Template Rejected
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {template.comment ||
              template.rejectionReason ||
              "No specific reason provided by the reviewer."}
          </Typography>
        </Alert>
      )}

      {/* Simulated Phone UI Preview */}
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: 340,
          border: "12px solid #2c2c2c",
          borderRadius: "36px",
          bgcolor: "#f5f6f8",
          position: "relative",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          overflow: "hidden",
        }}
      >
        {/* Fake Phone Notch */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 120,
            height: 24,
            bgcolor: "#2c2c2c",
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            zIndex: 10,
          }}
        />

        <Box
          sx={{
            p: 2,
            pt: 4,
            bgcolor: "white",
            color: "text.primary",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" textAlign="center">
            {templateName}
          </Typography>
        </Box>

        {/* --- SCROLLABLE PHONE CONTENT AREA --- */}
        <Box
          sx={{
            p: 2,
            height: 480,
            overflowY: "auto",
            // Custom thin scrollbar so it looks native to mobile
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {content ? (
            <Card
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                bgcolor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {/* Text + Media */}
              {content.type === "textWithMedia" && (
                <Box>
                  {content.messageText &&
                    content.messageOrder === "text_message_at_top" && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line", mb: 2, lineHeight: 1.6 }}
                      >
                        {content.messageText}
                      </Typography>
                    )}

                  {content.documentUrl && (
                    <Box sx={{ mb: 2 }}>
                      {content.isPdf ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            borderRadius: 2,
                            textAlign: "center",
                            bgcolor: "grey.50",
                          }}
                        >
                          <FileText
                            size={48}
                            color="#f44336"
                            style={{ marginBottom: 12 }}
                          />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {content.documentFileName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PDF Document
                          </Typography>
                        </Paper>
                      ) : (
                        <Box
                          component="img"
                          src={content.documentUrl}
                          alt={content.documentFileName}
                          sx={{
                            width: "100%",
                            maxHeight: 200,
                            objectFit: "cover",
                            borderRadius: 2,
                            bgcolor: "#000",
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {content.messageText &&
                    content.messageOrder !== "text_message_at_top" && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-line", mt: 2, lineHeight: 1.6 }}
                      >
                        {content.messageText}
                      </Typography>
                    )}
                </Box>
              )}

              {/* Plain Text */}
              {content.type === "plain" && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.5 }}
                >
                  {content.text}
                </Typography>
              )}

              {content.type === "standalone" && content.card && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <MessageCardPreview card={content.card} />
                </Box>
              )}

              {content.type === "carousel" && (
                <Box>
                  <Box
                    sx={{
                      mb: 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Images size={16} color="#6d4bd1" />
                      <Typography sx={{ fontSize: 12, fontWeight: 750 }}>
                        Carousel preview
                      </Typography>
                    </Stack>
                    <Chip
                      size="small"
                      label={`${content.cards.length} cards`}
                      sx={{
                        height: 22,
                        bgcolor: "#ede9fe",
                        color: "#5b4bb7",
                        fontSize: 10.5,
                        fontWeight: 750,
                      }}
                    />
                  </Box>

                  {content.cards.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        mx: -1,
                        px: 1,
                        pb: 1,
                        overflowX: "auto",
                        scrollSnapType: "x mandatory",
                        "&::-webkit-scrollbar": { height: 5 },
                        "&::-webkit-scrollbar-thumb": {
                          bgcolor: "#cbd2df",
                          borderRadius: 4,
                        },
                      }}
                    >
                      {content.cards.map((card, index) => (
                        <MessageCardPreview
                          key={`${
                            card.title || card.cardTitle || "card"
                          }-${index}`}
                          card={card}
                          cardNumber={index + 1}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        py: 3,
                        textAlign: "center",
                        fontSize: 12,
                        color: "text.secondary",
                      }}
                    >
                      This carousel does not contain any cards.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Suggestions Chips */}
              {content.suggestions?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {content.suggestions.map((suggestion, idx) => (
                      <Chip
                        key={idx}
                        label={getSuggestionLabel(suggestion)}
                        variant="outlined"
                        size="medium"
                        color="primary"
                        sx={{ borderRadius: 4, fontWeight: "500" }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Card>
          ) : (
            <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
              <Typography variant="body2">
                No preview content available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tech Details Section */}
      {!showLess && (
        <Box
          sx={{
            mt: 5,
            p: 3,
            bgcolor: "grey.50",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight="600"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Metadata
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Template ID
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {template.templateId || template.id || "Not available"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Customer ID
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {template.customerId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                User ID
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {template.userId || template.email || "Not available"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Updated On
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {formatTemplateDate(
                  template.updatedAt ||
                    template.updatedOn ||
                    getTemplateDate(template),
                  true
                )}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};
